import { Parser } from 'json2csv';
import { FormResponse, NetworkType, Survey, SurveyOptionType, WeightedOption, YourNetwork } from '@/types';

export type ExportGroup = 'Respondent' | 'Network Contact' | 'Relationships and Interactions';
export const EXPORT_GROUPS: ExportGroup[] = ['Respondent', 'Network Contact', 'Relationships and Interactions'];
type ExportRow = { info: FormResponse['info']; contact: YourNetwork };

export type ExportColumn = {
  id: string;
  fieldId: string;
  field: string;
  label: string;
  group: ExportGroup;
  variant?: 'Cleaned' | 'Raw';
  value: (row: ExportRow) => string | number;
};

const optionLabel = (options: SurveyOptionType[], key: number | null | undefined): string => options?.find((option) => option.key === key)?.label ?? '';
// Weight, not 1/0: the network analysis tools consume these columns as edge strengths.
const relationshipWeight = (row: ExportRow, option: WeightedOption): number => (row.contact.relationshipKeys?.includes(option.key) ? option.weight : 0);
const interactionWeight = (options: WeightedOption[], key: number | null | undefined): number => options?.find((option) => option.key === key)?.weight ?? 0;

const COLUMN_TITLES = {
  name: 'Name',
  email: 'Email',
  role: 'Role',
  organization: 'Organization',
  organizationType: 'Organization Type',
  zipCode: 'Zip Code',
  participationInNetworkType: 'Participation in Network Type',
  networkContact: 'My Network Contact',
  networkInteractions: 'My Network Interactions',
  networkInteractionWeights: 'My Network Interactions (Weights)'
} as const;

// Contact columns carry a prefix so they read apart from the respondent's own columns.
const identityPrefixes = {
  info: '',
  contact: `${COLUMN_TITLES.networkContact} `
} as const;

export const getExportColumns = (survey: Survey): ExportColumn[] => {
  const columns: ExportColumn[] = [];
  const addColumn = (id: string, field: string, group: ExportGroup, value: ExportColumn['value'], label = field) => {
    columns.push({ id, fieldId: id, field, label, group, value });
  };
  const addPair = (id: string, field: string, label: string, group: ExportGroup, clean: ExportColumn['value'], raw: ExportColumn['value']) => {
    columns.push(
      { id: `${id}.raw`, fieldId: id, field, label: `${label} (Raw)`, group, variant: 'Raw', value: raw },
      { id: `${id}.clean`, fieldId: id, field, label: `${label} (Cleaned)`, group, variant: 'Cleaned', value: clean }
    );
  };
  // Organization-only surveys never collect a contact's name, so those columns would always be blank.
  const namesCollected = survey.questions.network.networkType !== NetworkType.OrganizationOnly;
  const addIdentity = (source: keyof typeof identityPrefixes, group: ExportGroup) => {
    const prefix = identityPrefixes[source];
    if (source === 'info' || namesCollected) {
      addPair(
        `${source}.name`,
        COLUMN_TITLES.name,
        `${prefix}${COLUMN_TITLES.name}`,
        group,
        (row) => row[source]?.cleanName || row[source]?.name || '',
        (row) => row[source]?.name ?? ''
      );
    }
    if (source === 'info') {
      addColumn('info.email', COLUMN_TITLES.email, group, (row) => row.info?.email ?? '');
      addColumn('info.role', COLUMN_TITLES.role, group, (row) => row.info?.role ?? '');
    }
    addPair(
      `${source}.organization`,
      COLUMN_TITLES.organization,
      `${prefix}${COLUMN_TITLES.organization}`,
      group,
      (row) => row[source]?.cleanOrganization || row[source]?.organization || '',
      (row) => row[source]?.organization ?? ''
    );
    addPair(
      `${source}.organizationType`,
      COLUMN_TITLES.organizationType,
      `${prefix}${COLUMN_TITLES.organizationType}`,
      group,
      (row) => optionLabel(survey.organizationTypeList, row[source]?.cleanOrganizationType ?? row[source]?.organizationType),
      (row) => optionLabel(survey.organizationTypeList, row[source]?.organizationType)
    );
  };

  addIdentity('info', 'Respondent');
  addColumn('info.zipCode', COLUMN_TITLES.zipCode, 'Respondent', (row) => row.info?.zipCode ?? '');
  addColumn('info.participationInNetworkType', COLUMN_TITLES.participationInNetworkType, 'Respondent', (row) =>
    optionLabel(survey.participationNetworkTypeList, row.info?.participationInNetworkType)
  );
  addIdentity('contact', 'Network Contact');
  survey.questions.relationships.options.forEach((option) => {
    addColumn(`relationship.${option.key}`, option.label, 'Relationships and Interactions', (row) => relationshipWeight(row, option));
  });
  addColumn('contact.interaction', COLUMN_TITLES.networkInteractions, 'Relationships and Interactions', (row) =>
    optionLabel(survey.questions.interactions.options, row.contact.interactionKey)
  );
  addColumn('contact.interactionWeight', COLUMN_TITLES.networkInteractionWeights, 'Relationships and Interactions', (row) =>
    interactionWeight(survey.questions.interactions.options, row.contact.interactionKey)
  );
  return columns;
};

export const createResponseCSV = (responses: FormResponse[], columns: ExportColumn[], selectedIds: string[]): string => {
  const selected = new Set(selectedIds);
  const fields = columns.filter((column) => selected.has(column.id)).map(({ label, value }) => ({ label, value }));
  if (!fields.length) throw new Error('Select at least one column.');

  const rows = responses
    .filter((response) => response.completedOn != null)
    .flatMap((response) => (response.network?.yourNetwork ?? []).map((contact) => ({ info: response.info, contact })));
  return new Parser<ExportRow>({ fields }).parse(rows);
};

export const normalizeCSVFilename = (filename: string): string => {
  const sanitized = Array.from(filename.trim())
    .filter((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127)
    .join('')
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/[. ]+$/, '');
  const stem = sanitized.replace(/\.csv$/i, '').trim();
  if (!stem || /^\.+$/.test(stem)) return '';
  return /\.csv$/i.test(sanitized) ? sanitized : `${sanitized}.csv`;
};

export const downloadCSV = (csv: string, filename: string): void => {
  const normalized = normalizeCSVFilename(filename);
  if (!normalized) throw new Error('Enter a filename.');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = normalized;
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    // Let the browser start reading the Blob before releasing its URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
